import { useState } from "react";

interface HelpModalProps {
  onClose: () => void;
}

type HelpTab = "start" | "shortcuts" | "features" | "about";

const SHORTCUTS = [
  { group: "Viewport", keys: [
    ["Scroll", "Zoom in/out"],
    ["Drag", "Orbit (rotate view)"],
    ["Shift + Drag", "Pan"],
    ["F", "Fit to view"],
  ]},
  { group: "Editor", keys: [
    ["Ctrl/Cmd + Enter", "Execute script"],
    ["Ctrl/Cmd + Z", "Undo"],
    ["Ctrl/Cmd + Shift + Z", "Redo"],
  ]},
  { group: "Features", keys: [
    ["Delete / Backspace", "Delete selected feature"],
    ["Click mesh", "Select feature in tree"],
  ]},
  { group: "Sketch", keys: [
    ["L", "Line tool"],
    ["A", "Arc tool"],
    ["B", "Bezier tool"],
    ["P", "Spline tool"],
    ["S", "Select tool"],
    ["Escape", "Exit sketch"],
  ]},
  { group: "App", keys: [
    ["?", "Open this help"],
  ]},
];

export default function HelpModal({ onClose }: HelpModalProps) {
  const [tab, setTab] = useState<HelpTab>("start");

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="help-modal" onClick={(e) => e.stopPropagation()}>
        <div className="help-modal-header">
          <h2>Help</h2>
          <button className="help-modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="help-modal-tabs">
          {([
            ["start", "Get Started"],
            ["shortcuts", "Shortcuts"],
            ["features", "Features"],
            ["about", "About"],
          ] as [HelpTab, string][]).map(([key, label]) => (
            <button
              key={key}
              className={`help-tab ${tab === key ? "active" : ""}`}
              onClick={() => setTab(key)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="help-modal-body">
          {tab === "start" && (
            <div className="help-start">
              <div className="help-step">
                <div className="help-step-num">1</div>
                <div>
                  <strong>Add a shape</strong>
                  <p>Click Box, Cylinder, or Sphere in the toolbar to add a primitive. Or use Extrude/Revolve for profile-based shapes.</p>
                </div>
              </div>
              <div className="help-step">
                <div className="help-step-num">2</div>
                <div>
                  <strong>Modify properties</strong>
                  <p>Select a feature in the tree to edit its dimensions, position, and material in the property panel.</p>
                </div>
              </div>
              <div className="help-step">
                <div className="help-step-num">3</div>
                <div>
                  <strong>Combine and refine</strong>
                  <p>Use Booleans (Cut, Fuse) to combine shapes. Add Fillets, Chamfers, or Shell to refine edges.</p>
                </div>
              </div>
              <div className="help-step">
                <div className="help-step-num">4</div>
                <div>
                  <strong>Export</strong>
                  <p>Export as STL (3D printing), STEP (manufacturing), GLB (web), PNG (image), or 3MF (advanced 3D printing).</p>
                </div>
              </div>
              <div className="help-tip">
                <strong>Tip:</strong> Use the AI tab to describe shapes in plain English and get code generated automatically.
              </div>
            </div>
          )}

          {tab === "shortcuts" && (
            <div className="help-shortcuts">
              {SHORTCUTS.map((group) => (
                <div key={group.group} className="help-shortcut-group">
                  <div className="help-shortcut-group-title">{group.group}</div>
                  {group.keys.map(([key, desc]) => (
                    <div key={key} className="help-shortcut-row">
                      <kbd className="help-kbd">{key}</kbd>
                      <span>{desc}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {tab === "features" && (
            <div className="help-features">
              {([
                ["Design", "Feature tree, creation toolbar, property panel — build geometry with GUI controls"],
                ["Script", "View and edit the generated replicad JavaScript code directly"],
                ["AI", "Describe shapes in natural language — Claude generates the code"],
                ["Versions", "Git-like version control with branches, commits, and merge"],
                ["Motion", "Animate revolute/slider mates to study assembly motion (assembly only)"],
                ["Release", "Workflow states (WIP → review → released) with approval chains"],
                ["BOM", "Bill of Materials — flat, single-level, and multi-level views with CSV export"],
                ["ECOs", "Engineering Change Orders — track and approve design changes"],
              ] as [string, string][]).map(([name, desc]) => (
                <div key={name} className="help-feature-row">
                  <strong>{name}</strong>
                  <p>{desc}</p>
                </div>
              ))}
            </div>
          )}

          {tab === "about" && (
            <div className="help-about">
              <div className="help-about-logo">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="40" height="40">
                  <path d="M4 18L12 4L20 18H4Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>cad-plm</h3>
              <p className="help-about-tagline">Browser-native CAD + PLM</p>
              <div className="help-about-details">
                <p>Built with replicad + OCCT WASM + Three.js + Yjs</p>
                <p>Real-time collaboration via CRDTs</p>
                <p>Script-first parametric modeling</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
